import React from "react";
import { TrendingUp, ListChecks, Activity, Target, CheckCircle2, XCircle, AlertCircle, Banknote } from "lucide-react";

/**
 * Renders the expanded credit consultation sections produced by Sergeant Credit:
 *  - Score factors (reasons holding the score back)
 *  - Utilization analysis
 *  - Payment history analysis
 *  - Improvement plan
 *  - Payoff simulator
 *  - Funding readiness
 *
 * Each section gracefully no-ops if its data is missing (older analyses still render).
 */
const SEVERITY_STYLES = {
  high: "bg-red-500/15 text-red-300 border-red-500/30",
  medium: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  low: "bg-green-500/15 text-green-300 border-green-500/30",
};

const TIER_STYLES = {
  ready: { icon: CheckCircle2, color: "text-green-400", label: "Ready" },
  needs_work: { icon: AlertCircle, color: "text-amber-400", label: "Needs work" },
  not_yet: { icon: XCircle, color: "text-red-400", label: "Not yet" },
};

const FUNDING_LABELS = {
  mortgage_fha: "FHA Mortgage",
  mortgage_conventional: "Conventional Mortgage",
  business_funding: "Business Funding",
  auto_loan: "Auto Loan",
  personal_loan: "Personal Loan",
};

function Section({ title, icon: Icon, children, testid }) {
  return (
    <div className="p-3 rounded-lg bg-black/20 border border-white/5 mb-3" data-testid={testid}>
      <div className="flex items-center gap-2 mb-2.5">
        <Icon className="w-3.5 h-3.5 text-[var(--if-gold)]" />
        <span className="font-mono-label text-[var(--if-gold)]">{title}</span>
      </div>
      {children}
    </div>
  );
}

function fmtMoney(n) {
  if (n == null || !isFinite(n)) return "—";
  return `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

export default function AnalysisExtras({ r }) {
  if (!r) return null;

  return (
    <div className="mt-3" data-testid="analysis-extras">
      {r.summary?.narrative && (
        <p className="text-sm text-white/80 mb-3 leading-relaxed" style={{ fontFamily: "Inter, sans-serif" }}>
          {r.summary.narrative}
        </p>
      )}

      {/* Score factors */}
      {(r.score_factors || []).length > 0 && (
        <Section title="Why the score is held back" icon={Activity} testid="section-score-factors">
          <div className="space-y-1.5">
            {r.score_factors.map((f, i) => (
              <div key={`sf-${i}`} className="flex items-start gap-2 text-sm" style={{ fontFamily: "Inter, sans-serif" }}>
                <span className={`text-[10px] px-2 py-0.5 rounded-full border whitespace-nowrap shrink-0 ${SEVERITY_STYLES[f.severity] || SEVERITY_STYLES.low}`}>
                  {(f.severity || "low").toUpperCase()}
                </span>
                <div>
                  <div className="text-white/85"><strong>{f.factor}</strong></div>
                  <div className="text-white/55 text-xs">{f.explanation}</div>
                </div>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Utilization */}
      {r.utilization_analysis && Object.keys(r.utilization_analysis).length > 0 && (
        <Section title="Utilization analysis" icon={TrendingUp} testid="section-utilization">
          <div className="flex flex-wrap items-baseline gap-3 mb-2" style={{ fontFamily: "Inter, sans-serif" }}>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-white/45">Overall</div>
              <div className="font-display text-2xl text-white">
                {r.utilization_analysis.overall_pct != null ? `${r.utilization_analysis.overall_pct}%` : "—"}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-white/45">Status</div>
              <div className="font-display text-sm text-[var(--if-gold)] uppercase">
                {(r.utilization_analysis.status || "—").split(" ")[0]}
              </div>
            </div>
            <div className="ml-auto text-right">
              <div className="text-[10px] text-white/45">{fmtMoney(r.utilization_analysis.total_balances_usd)} / {fmtMoney(r.utilization_analysis.total_limits_usd)}</div>
            </div>
          </div>
          {r.utilization_analysis.narrative && (
            <p className="text-xs text-white/65 mb-2" style={{ fontFamily: "Inter, sans-serif" }}>{r.utilization_analysis.narrative}</p>
          )}
          {(r.utilization_analysis.per_account_top || []).length > 0 && (
            <div className="space-y-1 mt-2">
              <div className="font-mono-label text-white/45 text-[10px]">Top utilization accounts</div>
              {r.utilization_analysis.per_account_top.slice(0, 5).map((acc, i) => (
                <div key={`ua-${i}`} className="flex justify-between text-xs py-0.5" style={{ fontFamily: "Inter, sans-serif" }}>
                  <span className="text-white/75 truncate mr-2">{acc.creditor}</span>
                  <span className="text-white/55 whitespace-nowrap">{fmtMoney(acc.balance_usd)} / {fmtMoney(acc.limit_usd)} · <span className={acc.utilization_pct > 50 ? "text-red-300" : acc.utilization_pct > 30 ? "text-amber-300" : "text-green-300"}>{acc.utilization_pct}%</span></span>
                </div>
              ))}
            </div>
          )}
        </Section>
      )}

      {/* Payment history */}
      {r.payment_history_analysis && Object.keys(r.payment_history_analysis).length > 0 && (
        <Section title="Payment history" icon={ListChecks} testid="section-payment-history">
          <div className="grid grid-cols-4 gap-2 mb-2 text-center">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-white/45">On-time</div>
              <div className="font-display text-lg text-green-300">{r.payment_history_analysis.on_time_pct != null ? `${r.payment_history_analysis.on_time_pct}%` : "—"}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-white/45">30-day</div>
              <div className="font-display text-lg text-amber-300">{r.payment_history_analysis.total_late_30 ?? 0}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-white/45">60-day</div>
              <div className="font-display text-lg text-orange-300">{r.payment_history_analysis.total_late_60 ?? 0}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-white/45">90+ day</div>
              <div className="font-display text-lg text-red-300">{r.payment_history_analysis.total_late_90_plus ?? 0}</div>
            </div>
          </div>
          {r.payment_history_analysis.recent_lates_12mo > 0 && (
            <div className="text-xs text-amber-300/90 mb-1" style={{ fontFamily: "Inter, sans-serif" }}>
              ⚠ {r.payment_history_analysis.recent_lates_12mo} late payment{r.payment_history_analysis.recent_lates_12mo === 1 ? "" : "s"} in the last 12 months — most damaging window
            </div>
          )}
          {r.payment_history_analysis.narrative && (
            <p className="text-xs text-white/65" style={{ fontFamily: "Inter, sans-serif" }}>{r.payment_history_analysis.narrative}</p>
          )}
        </Section>
      )}

      {/* Improvement plan */}
      {(r.improvement_plan || []).length > 0 && (
        <Section title="Your improvement plan" icon={Target} testid="section-improvement-plan">
          <div className="space-y-2">
            {r.improvement_plan.slice(0, 8).map((step, i) => (
              <div key={`ip-${i}`} className="flex gap-3" style={{ fontFamily: "Inter, sans-serif" }}>
                <div className="font-display text-xl text-[var(--if-gold)] shrink-0 w-6 text-center">{step.order ?? i + 1}</div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <span className="text-sm text-white font-semibold">{step.title}</span>
                    {step.timeframe && <span className="text-[10px] text-white/45 uppercase tracking-wider">· {step.timeframe}</span>}
                    {step.projected_score_lift && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-green-500/15 text-green-300 border border-green-500/25">
                        {step.projected_score_lift}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-white/65 mt-0.5">{step.action}</div>
                </div>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Payoff simulator */}
      {(r.payoff_simulator || []).length > 0 && (
        <Section title="Payoff simulator — what moves the needle" icon={Banknote} testid="section-payoff-simulator">
          <div className="space-y-2">
            {r.payoff_simulator.slice(0, 6).map((p, i) => (
              <div key={`ps-${i}`} className="p-2 rounded border border-white/5 bg-black/15" style={{ fontFamily: "Inter, sans-serif" }}>
                <div className="flex justify-between items-baseline mb-1">
                  <span className="text-sm text-white truncate mr-2">{p.creditor}</span>
                  <span className="text-xs text-white/55 whitespace-nowrap">Currently at <span className="text-red-300">{p.current_util_pct}%</span></span>
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div className="p-1.5 rounded bg-amber-500/5 border border-amber-500/15">
                    <div className="text-[10px] text-amber-200/70 uppercase tracking-wider">Pay to 30%</div>
                    <div className="text-white">→ {fmtMoney(p.pay_to_30pct_balance_usd)}</div>
                    <div className="text-[10px] text-green-300">{p.projected_lift_if_paid_to_30}</div>
                  </div>
                  <div className="p-1.5 rounded bg-green-500/5 border border-green-500/15">
                    <div className="text-[10px] text-green-200/70 uppercase tracking-wider">Pay to 10%</div>
                    <div className="text-white">→ {fmtMoney(p.pay_to_10pct_balance_usd)}</div>
                    <div className="text-[10px] text-green-300">{p.projected_lift_if_paid_to_10}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Funding readiness */}
      {r.funding_readiness && Object.keys(r.funding_readiness).length > 0 && (
        <Section title="Funding readiness" icon={CheckCircle2} testid="section-funding-readiness">
          <div className="space-y-1.5">
            {Object.entries(r.funding_readiness).map(([key, val]) => {
              const tierStyle = TIER_STYLES[val?.tier] || TIER_STYLES.not_yet;
              const Icon = tierStyle.icon;
              return (
                <div key={key} className="flex items-start gap-2 py-1" style={{ fontFamily: "Inter, sans-serif" }} data-testid={`funding-${key}`}>
                  <Icon className={`w-4 h-4 ${tierStyle.color} shrink-0 mt-0.5`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between items-baseline gap-2">
                      <span className="text-sm text-white">{FUNDING_LABELS[key] || key}</span>
                      <span className={`text-[10px] uppercase tracking-wider ${tierStyle.color} whitespace-nowrap`}>{tierStyle.label}</span>
                    </div>
                    {val.gap && <div className="text-xs text-white/55 mt-0.5">{val.gap}</div>}
                    {(val.requirements || []).length > 0 && (
                      <ul className="mt-1 ml-1 space-y-0.5">
                        {val.requirements.slice(0, 4).map((req, ri) => (
                          <li key={`req-${ri}`} className="text-[11px] text-white/45">→ {req}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </Section>
      )}
    </div>
  );
}
